/**
 * Veyronix Commerce — All public pages (consolidated)
 * Routes:
 *   /commerce, /commerce/services, /commerce/dropshipping, /commerce/shopify,
 *   /commerce/automation, /commerce/pricing, /commerce/case-studies,
 *   /commerce/book-consultation, /commerce/contact, /commerce/insights, /commerce/insights/:slug,
 *   /privacy, /cookies, /terms, /consultancy-terms, /refund-policy
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/App";
import { Rocket, Package, Store, TrendingUp, Zap, ArrowRight, Mail, Phone, Calendar, CheckCircle, Sparkles, FileText } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const ICON_MAP = { Package, Store, TrendingUp, Zap, Rocket, Sparkles };

export const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-slate-900">
    <nav className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/commerce" className="flex items-center gap-3">
          <img src={LOGO} alt="V" className="h-9 w-auto brightness-0 invert" />
          <div className="h-7 w-px bg-white/30" />
          <span className="text-white font-brand font-bold">VEYRONIX <span className="text-indigo-300 text-xs ml-1 align-middle">COMMERCE</span></span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm">
          <Link to="/commerce/services" className="text-slate-300 hover:text-white">Services</Link>
          <Link to="/commerce/pricing" className="text-slate-300 hover:text-white">Pricing</Link>
          <Link to="/commerce/case-studies" className="text-slate-300 hover:text-white">Case Studies</Link>
          <Link to="/commerce/insights" className="text-slate-300 hover:text-white">Insights</Link>
          <Link to="/commerce/playbook" className="text-emerald-300 hover:text-white" data-testid="commerce-nav-playbook">Free Playbook</Link>
          <Link to="/commerce/contact" className="text-slate-300 hover:text-white">Contact</Link>
          <a href="https://www.veyronix.co.uk" className="text-slate-400 hover:text-white">Veyronix ↗</a>
        </div>
        <Link to="/commerce/book-consultation">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">Book Consultation</Button>
        </Link>
      </div>
    </nav>
    {children}
    <footer className="bg-slate-950 text-slate-400 py-10 mt-16">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-6 text-sm">
        <div>
          <p className="text-white font-brand font-bold mb-2">VEYRONIX COMMERCE</p>
          <p className="text-xs max-w-md">Veyronix Commerce is a trading division of Veyronix Limited, providing e-commerce consultancy, digital commerce development and business automation services.</p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/cookies" className="hover:text-white">Cookies</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
          <Link to="/consultancy-terms" className="hover:text-white">Consultancy T&Cs</Link>
          <Link to="/refund-policy" className="hover:text-white">Refund Policy</Link>
        </div>
      </div>
      <p className="text-center text-xs text-slate-600 mt-6">© {new Date().getFullYear()} Veyronix Limited</p>
    </footer>
  </div>
);

// ============ LANDING ============
export const CommerceLanding = () => {
  const [content, setContent] = useState({ services: [], pricing: [], case_studies: [] });
  useEffect(() => {
    fetch(`${API}/commerce/content`).then(r => r.json()).then(setContent).catch(() => {});
    document.title = "Veyronix Commerce — Build Smarter. Sell Globally. Scale Confidently.";
  }, []);

  return (
    <Shell>
      {/* Hero */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-slate-50">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-200/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-200/40 rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <span className="text-indigo-700 text-sm font-medium">A trading division of Veyronix Limited</span>
          </div>
          <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 leading-[1.05]">
            Build Smarter.<br />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">Sell Globally.</span><br />
            Scale Confidently.
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Launch, optimise and scale your e-commerce business with Veyronix Commerce. From dropshipping and Shopify development to AI-powered automation and strategic consulting — we turn digital opportunity into sustainable growth.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/commerce/book-consultation"><Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8" data-testid="hero-book-btn">Book a Free Consultation<ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
            <Link to="/commerce/services"><Button size="lg" variant="outline" className="rounded-full px-8" data-testid="hero-services-btn">Explore Services</Button></Link>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-brand text-3xl font-bold text-slate-900 mb-8 text-center">Our Services</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {content.services.map(s => {
            const Icon = ICON_MAP[s.icon] || Rocket;
            return (
              <div key={s.key} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-indigo-600" /></div>
                <h3 className="font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{s.description}</p>
                <Link to={`/commerce/${s.key}`} className="text-indigo-600 text-sm font-medium hover:underline inline-flex items-center gap-1">Learn more <ArrowRight className="h-3 w-3" /></Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-brand text-3xl font-bold text-slate-900 mb-2 text-center">Service Packages</h2>
          <p className="text-slate-500 text-center mb-10">Transparent pricing. Editable from the admin dashboard.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {content.pricing.map(p => (
              <div key={p.key} className={`rounded-2xl p-6 border ${p.is_featured ? "bg-gradient-to-br from-indigo-600 to-violet-700 text-white border-indigo-600" : "bg-white border-slate-200"}`}>
                <p className={`text-sm font-medium mb-2 ${p.is_featured ? "text-indigo-100" : "text-indigo-600"}`}>{p.name}</p>
                <p className="font-brand text-2xl font-bold mb-2">{p.price_display}</p>
                <p className={`text-sm mb-4 ${p.is_featured ? "text-indigo-100" : "text-slate-500"}`}>{p.tagline}</p>
                <ul className="space-y-2 mb-6 text-sm">
                  {p.bullets.map((b, i) => <li key={i} className="flex gap-2"><CheckCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${p.is_featured ? "text-indigo-200" : "text-emerald-500"}`} />{b}</li>)}
                </ul>
                <Link to={p.cta_href || "/commerce/book-consultation"}>
                  <Button className={`w-full rounded-full ${p.is_featured ? "bg-white text-indigo-700 hover:bg-indigo-50" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}>{p.cta_label}</Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 text-center mt-6 max-w-3xl mx-auto">Prices are indicative and may vary depending on project requirements. Third-party platform fees, advertising budgets and subscriptions are charged separately where applicable. Services do not guarantee sales or profits.</p>
        </div>
      </section>

      {/* Playbook */}
      <section className="py-14 bg-slate-900 text-white" data-testid="commerce-playbook-cta">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-emerald-300 text-xs font-semibold tracking-widest mb-2">FREE DOWNLOAD</p>
            <h2 className="font-brand text-2xl md:text-3xl font-bold mb-2">The UK Dropshipping Playbook</h2>
            <p className="text-slate-300 max-w-xl text-sm">UK legal set-up, VAT, supplier vetting, pricing for profit and a 90-day launch checklist — in one practical PDF.</p>
          </div>
          <Link to="/commerce/playbook"><Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full px-8">Get the Playbook</Button></Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-brand text-3xl font-bold text-slate-900 mb-4">Ready to grow your e-commerce business?</h2>
          <p className="text-slate-600 mb-8">Book a free, no-obligation consultation with Veyronix Commerce.</p>
          <Link to="/commerce/book-consultation"><Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8">Book Your Free Consultation</Button></Link>
        </div>
      </section>
    </Shell>
  );
};

// ============ GENERIC SERVICE DETAIL PAGE ============
export const CommerceServicePage = ({ serviceKey, title, intro }) => {
  const [service, setService] = useState(null);
  useEffect(() => {
    fetch(`${API}/commerce/content`).then(r => r.json()).then(c => setService(c.services.find(s => s.key === serviceKey)));
    document.title = `${title} | Veyronix Commerce`;
  }, [serviceKey, title]);
  const bullets = service?.bullets || [];
  return (
    <Shell>
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-brand text-4xl font-bold text-slate-900 mb-4">{service?.title || title}</h1>
        <p className="text-slate-600 text-lg mb-8">{service?.description || intro}</p>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-8">
          <h2 className="font-bold text-slate-900 mb-4">What's included</h2>
          <ul className="space-y-2">
            {bullets.map((b, i) => <li key={i} className="flex gap-3"><CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />{b}</li>)}
          </ul>
        </div>
        <Link to="/commerce/book-consultation"><Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 rounded-full">Book a Consultation<ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
      </section>
    </Shell>
  );
};

export const CommerceAllServices = () => <CommerceServicePage serviceKey="__none__" title="All Services" intro="Explore every Veyronix Commerce offering." />;

// ============ PRICING ============
export const CommercePricing = () => {
  const [pricing, setPricing] = useState([]);
  useEffect(() => { fetch(`${API}/commerce/content`).then(r => r.json()).then(c => setPricing(c.pricing)); document.title = "Pricing | Veyronix Commerce"; }, []);
  return (
    <Shell>
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h1 className="font-brand text-4xl font-bold text-slate-900 mb-4 text-center">Service Packages</h1>
        <p className="text-slate-500 text-center mb-12">Transparent pricing for every stage of your e-commerce journey.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {pricing.map(p => (
            <div key={p.key} className={`rounded-2xl p-6 border ${p.is_featured ? "bg-gradient-to-br from-indigo-600 to-violet-700 text-white" : "bg-white border-slate-200"}`}>
              <p className={`text-sm font-medium mb-2 ${p.is_featured ? "text-indigo-100" : "text-indigo-600"}`}>{p.name}</p>
              <p className="font-brand text-2xl font-bold mb-2">{p.price_display}</p>
              <p className={`text-sm mb-4 ${p.is_featured ? "text-indigo-100" : "text-slate-500"}`}>{p.tagline}</p>
              <ul className="space-y-2 mb-6 text-sm">
                {p.bullets.map((b, i) => <li key={i} className="flex gap-2"><CheckCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${p.is_featured ? "text-indigo-200" : "text-emerald-500"}`} />{b}</li>)}
              </ul>
              <Link to={p.cta_href || "/commerce/book-consultation"}>
                <Button className={`w-full rounded-full ${p.is_featured ? "bg-white text-indigo-700 hover:bg-indigo-50" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}>{p.cta_label}</Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 text-center mt-10 max-w-3xl mx-auto">Prices are indicative and may vary depending on project requirements. Third-party platform fees, advertising budgets and subscriptions are charged separately. Services do not guarantee sales or profits.</p>
      </section>
    </Shell>
  );
};

// ============ CASE STUDIES ============
export const CommerceCaseStudies = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { fetch(`${API}/commerce/content`).then(r => r.json()).then(c => setItems(c.case_studies)); document.title = "Case Studies | Veyronix Commerce"; }, []);
  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="font-brand text-4xl font-bold text-slate-900 mb-4 text-center">Case Studies</h1>
        <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto">Illustrative scenarios demonstrating how Veyronix Commerce engages with clients. Real client stories will replace these as our portfolio grows.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(cs => (
            <div key={cs.title} className="bg-white border border-slate-200 rounded-2xl p-6">
              {cs.is_illustrative && <span className="inline-block text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 mb-3">Illustrative scenario</span>}
              <p className="text-xs text-indigo-600 font-medium">{cs.industry}</p>
              <h3 className="font-bold text-slate-900 mt-1 mb-3">{cs.title}</h3>
              <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">Challenge</p>
              <p className="text-sm text-slate-700">{cs.challenge}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">Approach</p>
              <p className="text-sm text-slate-700">{cs.approach}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">Outcome</p>
              <p className="text-sm text-slate-700">{cs.outcome}</p>
            </div>
          ))}
        </div>
      </section>
    </Shell>
  );
};

// ============ BOOK CONSULTATION ============
export const CommerceBookConsultation = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", company_name: "", preferred_date: "", preferred_time: "10:00", service_interest: "General consultancy", description: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { document.title = "Book Consultation | Veyronix Commerce"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/commerce/book-consultation`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Booking failed");
      setDone(data);
      toast.success("Booking submitted!");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <Shell><section className="max-w-xl mx-auto px-6 py-20 text-center">
      <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
      <h1 className="font-brand text-3xl font-bold text-slate-900 mb-2">Booking submitted</h1>
      <p className="text-slate-600 mb-6">Reference: <b>{done.booking_ref}</b></p>
      <p className="text-sm text-slate-500">We'll confirm your consultation within 1 business day via email.</p>
    </section></Shell>
  );

  return (
    <Shell>
      <section className="max-w-xl mx-auto px-6 py-16">
        <h1 className="font-brand text-3xl font-bold text-slate-900 mb-2">Book a Free Consultation</h1>
        <p className="text-slate-500 mb-8">A 30-minute call to understand your goals and recommend next steps.</p>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="b-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="b-email" /></div>
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Company (optional)</Label><Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} /></div>
            <div><Label>Preferred date</Label><Input required type="date" value={form.preferred_date} onChange={e => setForm({ ...form, preferred_date: e.target.value })} data-testid="b-date" /></div>
            <div><Label>Preferred time</Label><Input required type="time" value={form.preferred_time} onChange={e => setForm({ ...form, preferred_time: e.target.value })} data-testid="b-time" /></div>
          </div>
          <div><Label>Service of interest</Label>
            <select value={form.service_interest} onChange={e => setForm({ ...form, service_interest: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
              <option>General consultancy</option><option>Dropshipping</option><option>Shopify / E-commerce Development</option><option>Growth Consultancy</option><option>AI & Automation</option>
            </select>
          </div>
          <div><Label>What would you like to discuss?</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <label className="flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" required checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })} /> I accept the <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link> and consent to being contacted about my enquiry.</label>
          <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="b-submit">{submitting ? "Submitting..." : "Book Consultation"}</Button>
        </form>
      </section>
    </Shell>
  );
};

// ============ CONTACT / ENQUIRY ============
export const CommerceContact = () => {
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", phone: "", business_type: "", services_needed: [], budget: "", preferred_start: "", description: "", source: "website", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { document.title = "Contact | Veyronix Commerce"; }, []);

  const toggleService = (s) => setForm(f => ({ ...f, services_needed: f.services_needed.includes(s) ? f.services_needed.filter(x => x !== s) : [...f.services_needed, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/commerce/enquiry`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submit failed");
      setDone(true);
      toast.success("Enquiry submitted!");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (done) return <Shell><section className="max-w-xl mx-auto px-6 py-20 text-center"><CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" /><h1 className="font-brand text-3xl font-bold">Thanks for your enquiry</h1><p className="text-slate-600 mt-2">We'll be in touch within 1 business day.</p></section></Shell>;

  return (
    <Shell>
      <section className="max-w-2xl mx-auto px-6 py-16">
        <h1 className="font-brand text-3xl font-bold text-slate-900 mb-2">Get in touch</h1>
        <p className="text-slate-500 mb-8">Tell us about your project — we'll respond within 1 business day.</p>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="enquiry-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name *</Label><Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="e-name" /></div>
            <div><Label>Company</Label><Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="e-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div><Label>Type of business</Label><Input value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })} placeholder="e.g. Fashion D2C, Marketplace reseller" /></div>
          <div>
            <Label>Services needed</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {["Dropshipping", "Shopify Development", "Growth Consultancy", "AI & Automation"].map(s => (
                <button type="button" key={s} onClick={() => toggleService(s)} className={`text-xs px-3 py-1 rounded-full border ${form.services_needed.includes(s) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-300 text-slate-700"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Estimated budget</Label>
              <select value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
                <option value="">Select</option><option>Under £1,000</option><option>£1,000–£5,000</option><option>£5,000–£20,000</option><option>£20,000+</option>
              </select>
            </div>
            <div><Label>Preferred start</Label><Input type="month" value={form.preferred_start} onChange={e => setForm({ ...form, preferred_start: e.target.value })} /></div>
          </div>
          <div><Label>Project description</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <label className="flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" required checked={form.consent} onChange={e => setForm({ ...form, consent: e.target.checked })} /> I accept the <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>.</label>
          <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="e-submit">{submitting ? "Submitting..." : "Submit Enquiry"}</Button>
        </form>
      </section>
    </Shell>
  );
};

// ============ INSIGHTS (BLOG) ============
export const CommerceInsights = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { fetch(`${API}/commerce/content`).then(r => r.json()).then(c => setPosts(c.blog_posts)); document.title = "Commerce Insights | Veyronix"; }, []);
  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="font-brand text-4xl font-bold mb-4 text-center">Commerce Insights</h1>
        <p className="text-slate-500 text-center mb-12">Practical guides for UK e-commerce entrepreneurs.</p>
        {posts.length === 0 ? (
          <p className="text-center text-slate-500">Articles coming soon — our Knowledge Centre launches shortly.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(p => (
              <Link key={p.slug} to={`/commerce/insights/${p.slug}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all">
                <h3 className="font-bold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-3">{p.excerpt}</p>
                <p className="text-xs text-indigo-600 mt-3">Read article →</p>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
};

export const CommerceInsightDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    fetch(`${API}/commerce/blog/${slug}`).then(r => r.ok ? r.json() : Promise.reject()).then(p => { setPost(p); document.title = `${p.title} | Veyronix Commerce`; }).catch(() => setNotFound(true));
  }, [slug]);
  if (notFound) return <Shell><section className="max-w-xl mx-auto px-6 py-20 text-center"><h1 className="font-brand text-2xl font-bold">Article not found</h1><Link to="/commerce/insights" className="text-indigo-600 hover:underline">← Back to Insights</Link></section></Shell>;
  if (!post) return <Shell><section className="max-w-xl mx-auto px-6 py-20 text-center text-slate-500">Loading...</section></Shell>;
  return (
    <Shell>
      <article className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/commerce/insights" className="text-indigo-600 text-sm hover:underline">← Back to Insights</Link>
        <h1 className="font-brand text-4xl font-bold text-slate-900 mt-4 mb-4">{post.title}</h1>
        <p className="text-slate-500 mb-8">{post.excerpt}</p>
        <div className="prose prose-slate max-w-none">
          {post.body_markdown.split("\n\n").map((para, i) => {
            if (para.startsWith("## ")) return <h2 key={i} className="font-brand text-2xl font-bold mt-8 mb-3">{para.slice(3)}</h2>;
            if (para.startsWith("**")) return <p key={i} className="font-semibold text-slate-800 mt-6">{para.replace(/\*\*/g, "")}</p>;
            return <p key={i} className="my-3 text-slate-700 leading-relaxed">{para}</p>;
          })}
        </div>
      </article>
    </Shell>
  );
};

// ============ LEGAL PAGES ============
// Internal status: these Commerce legal texts are still awaiting professional legal review (tracked internally, not shown publicly).
const LegalShell = ({ title, children }) => {
  useEffect(() => { document.title = `${title} | Veyronix`; }, [title]);
  return <Shell><section className="max-w-3xl mx-auto px-6 py-16" data-testid="commerce-legal-page">
    <h1 className="font-brand text-3xl font-bold mb-8">{title}</h1>
    <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3">{children}</div>
  </section></Shell>;
};

export const PrivacyPage = () => (
  <LegalShell title="Privacy Policy">
    <p>Veyronix Commerce (a trading division of Veyronix Limited) ("we", "us", "our") is committed to protecting the privacy of personal data submitted via this website.</p>
    <h2>1. Data we collect</h2><p>Name, email address, telephone number (if provided), company name, project details and any additional information you choose to share via our enquiry or booking forms. We also collect limited technical data (IP address, browser type, pages visited) through analytics cookies.</p>
    <h2>2. Lawful basis</h2><p>We process personal data under the lawful basis of (a) consent for marketing communications; (b) legitimate interest for responding to enquiries and operating our website; (c) contract performance where we have engaged with you commercially.</p>
    <h2>3. How we use your data</h2><p>To respond to enquiries, schedule consultations, deliver contracted services, send service-related communications, improve our website and comply with legal obligations.</p>
    <h2>4. Sharing</h2><p>We do not sell personal data. We share data only with trusted service providers (hosting, email, CRM) under appropriate data-processing agreements and with regulatory authorities where legally required.</p>
    <h2>5. Retention</h2><p>Enquiry data is retained for up to 24 months unless you request earlier deletion. Contract-related data is retained in line with UK statutory requirements (typically 6 years).</p>
    <h2>6. Your rights (UK GDPR)</h2><p>You have the right to access, rectify, delete, restrict or port your personal data, and to object to processing. Contact: privacy@veyronix.co.uk. You may also complain to the UK Information Commissioner's Office (ICO).</p>
    <h2>7. Cookies</h2><p>See our <Link to="/cookies" className="text-indigo-600 hover:underline">Cookie Policy</Link>.</p>
    <h2>8. Contact</h2><p>Veyronix Limited, United Kingdom. Email: privacy@veyronix.co.uk</p>
  </LegalShell>
);

export const CookiesPage = () => (
  <LegalShell title="Cookie Policy">
    <h2>What are cookies?</h2><p>Small text files placed on your device to help the website function and provide analytics information.</p>
    <h2>Types of cookies we use</h2>
    <p><b>Strictly necessary:</b> Required for the website to operate (authentication, security).<br/><b>Analytics:</b> Help us understand how visitors use the site (anonymised).<br/><b>Marketing:</b> Only with your explicit consent.</p>
    <h2>Managing cookies</h2><p>You can accept or decline non-essential cookies at any time via your browser settings. Rejecting cookies may impact website functionality.</p>
  </LegalShell>
);

export const TermsPage = () => (
  <LegalShell title="Website Terms of Use">
    <p>By accessing www.veyronix.co.uk you agree to these Terms of Use.</p>
    <h2>Intellectual property</h2><p>All content, trademarks and software are owned by Veyronix Limited or its licensors.</p>
    <h2>Acceptable use</h2><p>You must not misuse this website, including by introducing malware, unauthorised access, or scraping without permission.</p>
    <h2>Disclaimers</h2><p>Content is provided "as is". We make no warranty regarding uninterrupted availability or absence of errors.</p>
    <h2>Limitation of liability</h2><p>To the maximum extent permitted by law, Veyronix Limited is not liable for indirect or consequential loss.</p>
    <h2>Governing law</h2><p>These terms are governed by the laws of England and Wales.</p>
  </LegalShell>
);

export const ConsultancyTermsPage = () => (
  <LegalShell title="Consultancy Terms and Conditions">
    <h2>1. Scope</h2><p>These terms govern consultancy, development and advisory services provided by Veyronix Commerce ("Services"). Each engagement is confirmed via a Statement of Work (SOW) or written proposal.</p>
    <h2>2. Fees and payment</h2><p>Fees are quoted in the applicable SOW. Standard payment terms are 50% on acceptance, 50% on delivery, unless otherwise agreed. Invoices are payable within 14 days.</p>
    <h2>3. Deliverables and timelines</h2><p>Timelines are estimates based on timely client input. Delays caused by missing client content, approvals or third-party dependencies may extend delivery dates.</p>
    <h2>4. Client responsibilities</h2><p>Clients must provide accurate information, timely feedback, lawful rights to all supplied materials, and access to necessary accounts (e.g. Shopify, Google, payment providers).</p>
    <h2>5. Intellectual property</h2><p>Upon full payment, deliverables transfer to the client, excluding pre-existing Veyronix tooling and third-party components (which are licensed, not transferred).</p>
    <h2>6. Warranties</h2><p>We warrant professional, workmanlike delivery. We do not guarantee specific sales, revenue or ranking outcomes.</p>
    <h2>7. Confidentiality</h2><p>Both parties agree to keep confidential information private and not disclose it to third parties except as required by law.</p>
    <h2>8. Termination</h2><p>Either party may terminate for material breach with 14 days' written notice. Client remains liable for work completed up to termination.</p>
    <h2>9. Governing law</h2><p>England and Wales.</p>
  </LegalShell>
);

export const RefundPage = () => (
  <LegalShell title="Cancellation and Refund Policy">
    <h2>Consultations</h2><p>Free consultations may be cancelled or rescheduled with 24 hours' notice without charge.</p>
    <h2>One-time packages (Launch, Growth)</h2><p>If cancelled within 7 days of booking and prior to work commencing, a full refund is available. Once work has commenced, fees are prorated to work completed.</p>
    <h2>Monthly packages (Managed Commerce)</h2><p>Cancellable with 30 days' written notice. No refunds for the current billing period.</p>
    <h2>Statutory rights</h2><p>These terms do not affect your statutory rights under UK consumer protection law.</p>
    <h2>Contact</h2><p>Email: billing@veyronix.co.uk</p>
  </LegalShell>
);
